import XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";

export const exportToExcel = (confirmations) => {
  if (!confirmations || confirmations.length === 0) {
    throw new Error("No data to export.");
  }

  // 1. Prepare data for Excel
  const dataForExcel = confirmations.map((item) => {
    // Format timestamp
    const formatDate = (timestamp) => {
      if (!timestamp) return "-";
      let date;
      if (timestamp.seconds) {
        date = new Date(timestamp.seconds * 1000);
      } else if (typeof timestamp.toDate === "function") {
        date = timestamp.toDate();
      } else {
        date = new Date(timestamp);
        if (isNaN(date.getTime())) return String(timestamp);
      }

      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");

      return `${day}.${month}.${year} • ${hours}:${minutes}`;
    };

    // Format Side
    const side = item.invitationSide
      ? item.invitationSide.charAt(0).toUpperCase() +
        item.invitationSide.slice(1)
      : "";

    // Format Attendance
    let attendance = item.attendance || "";
    if (attendance === "not_sure") attendance = "Not Sure";
    else if (attendance)
      attendance = attendance.charAt(0).toUpperCase() + attendance.slice(1);

    // Format Children (+ count + ages)
    let children = "No";
    if (item.children) {
      children = `Yes (${item.childrenCount || 0})`;
      if (item.childrenAges) {
        const ages = Array.isArray(item.childrenAges)
          ? item.childrenAges.join(", ")
          : item.childrenAges;
        if (ages) children += `\nAges: ${ages}`; // Use newline for Excel wrapping
      }
    }

    // Format Plus One
    const plusOne = item.plusOne ? `Yes (${item.plusOneCount || 1})` : "No";

    return {
      Name: item.name || "",
      Email: item.email || "",
      Phone: item.phone || "",
      Side: side,
      Attendance: attendance,
      "+1": plusOne,
      Children: children,
      Notes: item.requirements || "",
      "Submitted At": formatDate(item.createdAt),
      // Hidden field for sorting
      _rawDate: item.createdAt
        ? item.createdAt.seconds || new Date(item.createdAt).getTime() / 1000
        : 0,
    };
  });

  // Sort by date descending
  dataForExcel.sort((a, b) => b._rawDate - a._rawDate);
  // Remove helper key
  const finalData = dataForExcel.map(({ _rawDate, ...rest }) => rest);

  // 2. Create Workbook and Worksheet
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.json_to_sheet(finalData);

  // 3. Define Column Widths
  const wscols = [
    { wch: 25 }, // Name
    { wch: 30 }, // Email
    { wch: 20 }, // Phone
    { wch: 10 }, // Side
    { wch: 15 }, // Attendance
    { wch: 15 }, // +1
    { wch: 30 }, // Children
    { wch: 50 }, // Notes (wide for wrapping)
    { wch: 20 }, // Submitted At
  ];
  worksheet["!cols"] = wscols;

  // 4. APPLY STYLES
  // We need to iterate over every cell in the range to apply borders and fonts.
  const range = XLSX.utils.decode_range(worksheet["!ref"]);

  for (let R = range.s.r; R <= range.e.r; ++R) {
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const cell_address = XLSX.utils.encode_cell({ r: R, c: C });
      if (!worksheet[cell_address]) continue;

      // Base Style: Border + Vertical Center + Wrap Text
      worksheet[cell_address].s = {
        border: {
          top: { style: "thin", color: { auto: 1 } },
          bottom: { style: "thin", color: { auto: 1 } },
          left: { style: "thin", color: { auto: 1 } },
          right: { style: "thin", color: { auto: 1 } },
        },
        alignment: {
          vertical: "center",
          wrapText: true,
        },
        font: { name: "Arial", sz: 10 },
      };

      // Header Row (Row 0)
      if (R === 0) {
        worksheet[cell_address].s.font = {
          name: "Arial",
          sz: 12,
          bold: true,
          color: { rgb: "000000" },
        };
        worksheet[cell_address].s.fill = { fgColor: { rgb: "E0E0E0" } }; // Light Gray
        worksheet[cell_address].s.alignment.horizontal = "center";
      }
      // Data Rows
      else {
        // Bold "Name" Column (Column 0)
        if (C === 0) {
          worksheet[cell_address].s.font.bold = true;
        }

        // Center Alignment for specific columns:
        // Phone(2), Side(3), Attendance(4), +1(5), Children(6), Submitted At(8)
        if ([2, 3, 4, 5, 6, 8].includes(C)) {
          worksheet[cell_address].s.alignment.horizontal = "center";
        }
      }
    }
  }

  // Append sheet to workbook
  XLSX.utils.book_append_sheet(workbook, worksheet, "Confirmations");

  // 5. Write file
  const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const data = new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
  });

  saveAs(data, "wedding_confirmations.xlsx");
};
